/**
 * MeetingRecordingPlayer — lazy-mounted <video> for meeting recordings.
 * Lazy-mounts <video> because the endpoint returns a Range-enabled video stream.
 *
 * Design notes:
 * - The <video> element stays mounted once the user first hits Play so that
 *   toggling Pause/Play doesn't reset the playback position.
 * - active state is kept in sync with the native play/pause events so that
 *   external controls (e.g. browser media keys) don't desync the button label.
 * - A 401/403 network error surfaces an actionable "session expired" message
 *   rather than silently showing "unavailable".
 */

import { useState, useRef, useCallback } from 'react';
import { Play, Pause, Video } from 'lucide-react';
import { getMeetingRecordingUrl } from '../api';

interface MeetingRecordingPlayerProps {
  meetingId: string;
}

type ErrorKind = 'session' | 'generic' | null;

export function MeetingRecordingPlayer({ meetingId }: MeetingRecordingPlayerProps) {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(false);
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const src = getMeetingRecordingUrl(meetingId);

  const handleToggle = useCallback(() => {
    if (!mounted) {
      // First play — mount the element and let autoPlay handle it
      setMounted(true);
      setActive(true);
      setErrorKind(null);
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    if (active) {
      video.pause();
    } else {
      void video.play();
    }
  }, [mounted, active]);

  const handleError = useCallback(() => {
    const video = videoRef.current;
    // MediaError doesn't expose HTTP status; check the network state instead.
    // NETWORK_NO_SOURCE (3) after a load attempt usually means a 401/403/404.
    // Guard against jsdom / test environments where HTMLMediaElement constants
    // or MediaError may not be fully defined.
    const networkNoSource =
      typeof HTMLMediaElement !== 'undefined' &&
      HTMLMediaElement.NETWORK_NO_SOURCE !== undefined
        ? HTMLMediaElement.NETWORK_NO_SOURCE
        : 3;
    const isAuthError = video?.networkState === networkNoSource;
    setErrorKind(isAuthError ? 'session' : 'generic');
    setActive(false);
  }, []);

  if (errorKind === 'session') {
    return (
      <span className="text-sm text-destructive">
        Session expired — please{' '}
        <button
          type="button"
          className="underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          onClick={() => window.location.reload()}
        >
          reload the page
        </button>{' '}
        to watch the recording.
      </span>
    );
  }

  if (errorKind === 'generic') {
    return (
      <span className="text-sm text-muted-foreground">Recording unavailable</span>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-label={active ? 'Pause recording' : 'Play recording'}
        aria-pressed={active}
        onClick={handleToggle}
        className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        <Video className="h-4 w-4" aria-hidden="true" />
        {active ? (
          <>
            <Pause className="h-3.5 w-3.5" aria-hidden="true" />
            Pause video
          </>
        ) : (
          <>
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Play recording
          </>
        )}
      </button>
      {/* Keep the video mounted once opened so toggling pause doesn't reset position */}
      <video
        ref={videoRef}
        autoPlay={mounted && active}
        controls
        preload="none"
        src={mounted ? src : undefined}
        aria-label="Meeting recording"
        onPlay={() => setActive(true)}
        onPause={() => setActive(false)}
        onEnded={() => setActive(false)}
        onError={handleError}
        className={`w-full rounded-md border border-border max-h-64 ${mounted ? '' : 'hidden'}`}
      />
    </div>
  );
}
